import { useState, useEffect } from "react";

const services = [
  {
    number: "01",
    title: "Root Cause Identification",
    description:
      "We specialize in Root Cause Analysis (RCA) to uncover the heart of the problem — whether it stems from ERP implementation challenges, inefficient internal processes, or other underlying factors. By identifying root causes, we provide sustainable solutions that prevent recurring issues and optimize your operations.",
    image: "https://ik.imagekit.io/kqumni8l2/INFOR_SERVICES_IMAGES/52%201.png?updatedAt=1737450587042",
  },
  {
    number: "02",
    title: "Continuous Improvement & Support",
    description:
      "Kapil Tech remains your trusted partner long after implementation — providing Infor LX expert access, custom report writing, system conversions, database administration, and targeted training workshops to ensure your system delivers value as your business evolves.",
    image: "https://ik.imagekit.io/7wqj9br0b/PTKCS/improvement.png",
  },
  {
    number: "03",
    title: "Comprehensive System Integration",
    description:
      "Our team ensures your Infor ERP integrates seamlessly with your existing tech stack via Infor ION or DCKAP Integrator API. We handle system customization, data migration, and API integration so operations remain uninterrupted and aligned to your business processes.",
    image: "https://ik.imagekit.io/kqumni8l2/INFOR_SERVICES_IMAGES/1.jpg?updatedAt=1737449856837",
  },
  {
    number: "04",
    title: "Full Service Experience",
    description:
      "Building on years of experience and hundreds of successful engagements, Kapil Tech is your trusted partner for ongoing Infor ERP support. Our CMMI Level 3 certified team delivers expert consulting, custom application development, process integration, and cloud solutions.",
    image: "https://ik.imagekit.io/kqumni8l2/INFOR_SERVICES_IMAGES/54%201.png?updatedAt=1737450589564",
  },
  {
    number: "05",
    title: "Strategic Infor Implementation",
    description:
      "We assess your business processes and identify where Infor ERP delivers the most value. Our consultants design and implement a tailored solution — cloud or on-premise — that aligns with your specific needs, improves efficiency, reduces costs, and supports your long-term goals.",
    image: "https://ik.imagekit.io/kqumni8l2/INFOR_SERVICES_IMAGES/53%201.png?updatedAt=1737450587154",
  },
  {
    number: "06",
    title: "Procedure Development",
    description:
      "Software should support your processes, not dictate them. We collaborate with you to develop customized procedures that strategically leverage Infor ERP's powerful features, ensuring the system aligns with your unique business needs and optimizes operational efficiency.",
    image: "https://ik.imagekit.io/kqumni8l2/INFOR_SERVICES_IMAGES/2.jpg?updatedAt=1737449874664",
  },
];

export default function InforServices() {
  const [hovered, setHovered] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile viewport to switch UI layouts
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return (
    <div
      className="w-full py-16 lg:py-24 px-4 sm:px-6 lg:px-8"
      style={{ backgroundColor: "#FFFFFF", fontFamily: "'Inter', sans-serif" }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-10 lg:mb-14 max-w-3xl">
          <div className="flex items-center gap-3 mb-4">
            <span
              className="inline-block w-8 h-[3px] rounded-full"
              style={{ backgroundColor: "#2563EB" }}
            />
            <span
              className="text-xs font-bold tracking-[0.2em] uppercase"
              style={{ color: "#2563EB" }}
            >
              Proven Methodology
            </span>
          </div>
          <h2 
            className="text-3xl sm:text-3xl md:text-4xl font-semibold leading-tight mb-6" 
            style={{ color: "#0A1A3A" }}
          >
            Our Implementation Approach
          </h2>
          <p 
            className="text-base sm:text-lg leading-relaxed font-normal" 
            style={{ color: "#4A5568" }}
          >
            At KCS Tech, we follow a structured, client-centric approach to every Infor ERP engagement —
            from root cause analysis to seamless integration and ongoing optimization, built to minimize
            risk and maximize long-term value.
          </p>
        </div>

        {/* Responsive Container */}
        <div className="flex flex-col lg:flex-row w-full lg:h-[540px] gap-6 lg:gap-[10px]">
          {services.map((service, i) => {
            // --- MOBILE SIMPLE CARD UI ---
            if (isMobile) {
              return (
                <div
                  key={i}
                  className="relative w-full rounded-2xl overflow-hidden flex flex-col transition-all duration-300 hover:shadow-lg"
                  style={{ 
                    backgroundColor: "#FFFFFF", 
                    border: "1px solid rgba(37, 99, 235, 0.15)", 
                    boxShadow: "0 4px 6px -1px rgba(10, 26, 58, 0.05)" 
                  }}
                >
                  {/* Compact Image Section */}
                  <div
                    className="w-full h-48 bg-cover bg-center"
                    style={{ backgroundImage: `url(${service.image})` }}
                  />
                  
                  {/* Content Section */}
                  <div className="p-6 flex flex-col flex-grow">
                    <span
                      className="text-xs font-bold tracking-wider uppercase mb-2"
                      style={{ color: "#2563EB" }}
                    >
                      {service.number}
                    </span>
                    <h3
                      className="text-xl font-semibold mb-3"
                      style={{ color: "#0A1A3A", fontFamily: "'Bricolage Grotesque', sans-serif" }}
                    >
                      {service.title}
                    </h3>
                    <p
                      className="text-base leading-relaxed flex-grow"
                      style={{ color: "#4A5568" }}
                    >
                      {service.description}
                    </p>
                  </div>
                </div>
              );
            }

            // --- DESKTOP ACCORDION UI (Unchanged Logic) ---
            const isHovered = hovered === i;
            const isAnyHovered = hovered !== null;
            const isCollapsed = isAnyHovered && !isHovered;

            return (
              <div
                key={i}
                className="relative overflow-hidden cursor-pointer flex-shrink-0 rounded-2xl"
                style={{
                  width: isHovered ? "36%" : isCollapsed ? "10%" : "16.66%",
                  transition: "width 0.55s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.4s ease",
                  minWidth: 0,
                  height: "100%",
                  border: "1.5px solid rgba(255,255,255,0.1)",
                }}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
              >
                {/* FULL BACKGROUND IMAGE — always visible */}
                <div
                  className="absolute inset-0 bg-cover bg-center"
                  style={{
                    backgroundImage: `url(${service.image})`,
                    transform: isHovered ? "scale(1.08)" : "scale(1)",
                    transition: "transform 0.65s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                />
                
                {/* Base subtle vignette — keeps image readable in collapsed state */}
                <div
                  className="absolute inset-0"
                  style={{
                    background: "linear-gradient(to bottom, rgba(26,38,52,0.25) 0%, rgba(26,38,52,0.05) 30%, rgba(26,38,52,0.15) 60%, rgba(26,38,52,0.55) 100%)",
                    transition: "opacity 0.45s ease",
                  }}
                />

                {/* Hover overlay — dark gradient from bottom for text readability */}
                <div
                  className="absolute inset-0"
                  style={{
                    background: "linear-gradient(to top, rgba(26,38,52,0.97) 0%, rgba(26,38,52,0.88) 35%, rgba(26,38,52,0.55) 60%, rgba(26,38,52,0.15) 80%, transparent 100%)",
                    opacity: isHovered ? 1 : 0,
                    transition: "opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                />

                {/* Accent top bar on hover */}
                <div
                  className="absolute top-0 left-0 right-0 z-10"
                  style={{
                    height: "3px",
                    backgroundColor: "#2563EB",
                    transform: isHovered ? "scaleX(1)" : "scaleX(0)",
                    transformOrigin: "left",
                    transition: "transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                />

                {/* Vertical title (collapsed state) */}
                <div
                  className="absolute inset-0 z-10 flex items-center justify-center"
                  style={{
                    opacity: isHovered ? 0 : 1,
                    transition: "opacity 0.35s ease",
                    pointerEvents: "none",
                  }}
                >
                  <h3
                    style={{
                      writingMode: "vertical-rl",
                      transform: "rotate(180deg)",
                      color: "#ffffff",
                      fontSize: "18px",
                      fontWeight: 600,
                      letterSpacing: "0.08em",
                      fontFamily: "'Bricolage Grotesque', sans-serif",
                      textAlign: "center",
                    }}
                  >
                    {service.title}
                  </h3>
                </div>

                {/* Expanded state: content anchored to bottom */}
                <div
                  className="absolute bottom-0 left-0 right-0 z-10"
                  style={{
                    padding: "24px 22px 24px",
                    opacity: isHovered ? 1 : 0,
                    transform: isHovered ? "translateY(0)" : "translateY(18px)",
                    transition: "opacity 0.45s ease 0.12s, transform 0.45s ease 0.12s",
                    pointerEvents: isHovered ? "auto" : "none",
                  }}
                >
                  <h3
                    className="font-semibold leading-tight mb-3"
                    style={{
                      color: "#ffffff",
                      fontFamily: "'Bricolage Grotesque', sans-serif",
                      fontSize: "18px",
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {service.title}
                  </h3>

                  <p
                    className="text-md leading-relaxed font-light"
                    style={{ 
                      color: "#ffffff", 
                      wordBreak: 'normal', 
                      overflowWrap: 'normal', 
                      hyphens: 'none', 
                      whiteSpace: 'normal' 
                    }}
                  >
                    {service.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}